import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { toast } from 'react-toastify';

const Header = () => {
  const { isLoggedIn, user, logout } = useAuth();
  const { settings, updateMaintenanceMode } = useSettings();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    setMobileOpen(false);
    navigate('/');
  };

  const toggleMaintenance = async () => {
    const newStatus = !settings?.maintenanceMode;
    const success = await updateMaintenanceMode(newStatus);
    if (success) {
      toast.success(`Maintenance Mode ${newStatus ? 'Activated' : 'Deactivated'}`);
    } else {
      toast.error('Failed to update maintenance mode');
    }
  };

  const initials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ').filter(Boolean);
    return (parts[0]?.[0] || 'U') + (parts[1]?.[0] || '');
  };

  return (
    <header className="sticky top-0 z-50 bg-gray-950/70 backdrop-blur supports-[backdrop-filter]:bg-gray-950/60 border-b border-gray-800/80 shadow-[0_1px_0_0_rgba(99,102,241,0.15)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 gap-3 sm:gap-6">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center gap-3">
              {/* use the same logo image as the footer (public/logo.png) */}
              <img
                className="h-9 w-auto sm:h-10 drop-shadow-lg"
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="ReferMe Logo"
              />
            </Link>
          </div>

          {/* Primary nav removed as requested */}

          {/* Desktop actions */}
          <nav className="hidden md:flex items-center gap-2 sm:gap-3 ml-auto" aria-label="Primary">
            {!isLoggedIn && (
              <>
                <Link
                  to="/login"
                  className="px-3 py-2 rounded-md text-sm font-medium border border-gray-700 text-gray-200 hover:bg-gray-800/60 transition"
                >
                  Login
                </Link>
                <Link
                  to="/search"
                  className="px-3.5 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow"
                >
                  Get Notes
                </Link>
              </>
            )}

            {isLoggedIn && user?.role === 'student' && (
              <>
                <Link to="/search" className="px-3.5 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow">Get Notes</Link>

                <div className="relative" ref={dropdownRef}>
                  <button
                    aria-haspopup="true"
                    aria-expanded={dropdownOpen}
                    onClick={() => setDropdownOpen((s) => !s)}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-md hover:bg-gray-800/70 transition"
                  >
                    <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 text-gray-200 flex items-center justify-center font-medium ring-1 ring-gray-600/40">
                      {initials(user?.firstname || user?.email || '')}
                    </div>
                    <svg
                      className="h-4 w-4 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      ></path>
                    </svg>
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-44 bg-gray-900/95 border border-gray-700 rounded-lg shadow-xl py-1 backdrop-blur">
                      <Link
                        to="/profile"
                        onClick={() => setDropdownOpen(false)}
                        className="block px-3 py-2 text-sm text-gray-200 hover:bg-gray-800/80 transition"
                      >
                        Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left block px-3 py-2 text-sm text-red-300 hover:bg-red-500/10 transition"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {isLoggedIn && user?.role === 'admin' && (
              <>
                <button
                  onClick={toggleMaintenance}
                  className={`px-3 py-2 rounded-md text-sm font-medium border ${settings?.maintenanceMode ? 'border-amber-500 text-amber-500 bg-amber-500/10' : 'border-gray-700 text-gray-400 hover:bg-gray-800'} transition`}
                >
                  {settings?.maintenanceMode ? '🛠 Maintenance ON' : '🛠 Maintenance OFF'}
                </button>
                <Link to="/branches" className="px-3.5 py-2 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow">Upload</Link>
                <Link to="/admin/purchases" className="px-3 py-2 rounded-md text-sm font-medium border border-gray-700 text-gray-300 bg-gray-800 hover:bg-gray-700 transition">Purchases</Link>
                <Link to="/admin/users" className="px-3 py-2 rounded-md text-sm font-medium border border-gray-700 text-gray-300 bg-gray-800 hover:bg-gray-700 transition">Users</Link>
                <Link to="/admin/pdfs" className="px-3 py-2 rounded-md text-sm font-medium border border-gray-700 text-gray-300 bg-gray-800 hover:bg-gray-700 transition">PDFs</Link>
                <button
                  onClick={handleLogout}
                  className="px-3 py-2 rounded-md text-sm font-medium border border-red-500/60 text-red-300 hover:bg-red-500/10 transition"
                >
                  Logout
                </button>
              </>
            )}
          </nav>

          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-2 ml-auto">
            {!isLoggedIn ? (
              <>
                <Link
                  to="/login"
                  className="px-3 py-1.5 rounded-md text-sm font-medium border border-gray-700 text-gray-200 hover:bg-gray-800/70 transition"
                >
                  Login
                </Link>
                <Link
                  to="/search"
                  className="px-3.5 py-1.5 rounded-md text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-500 transition shadow"
                >
                  Get Notes
                </Link>
              </>
            ) : (
              <button
                onClick={() => setMobileOpen((s) => !s)}
                aria-label="Open profile menu"
                className="inline-flex items-center justify-center p-1 rounded-md text-gray-300 hover:bg-gray-800/70 transition"
              >
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 text-gray-200 flex items-center justify-center font-medium ring-1 ring-gray-600/40">
                  {initials(user?.firstname || user?.email || '')}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile anchored panel (below profile icon) */}
      {isLoggedIn && mobileOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setMobileOpen(false)}>
          {/* semi-transparent backdrop */}
          <div className="absolute inset-0 bg-black/30" />

          {/* anchored panel below header/profile icon */}
          <div
            className="absolute top-14 right-4 w-64 sm:w-72 md:w-80 bg-gray-900/95 border border-gray-800 rounded-lg p-3 backdrop-blur shadow-lg transform transition duration-150 ease-out"
            style={{ boxShadow: '0 8px 20px rgba(2,6,23,0.55)' }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-800 text-gray-200 flex items-center justify-center font-medium ring-1 ring-gray-600/40 text-sm">
                  {initials(user?.firstname || user?.email || '')}
                </div>
                <div>
                  <div className="text-sm text-gray-300 leading-tight">{isLoggedIn ? `${user?.firstname || ''} ${user?.lastname || ''}`.trim() : 'Welcome'}</div>
                  <div className="text-xs text-gray-500 truncate max-w-[11rem]">{isLoggedIn ? user?.email : 'Sign in to access more'}</div>
                </div>
              </div>
              <button onClick={() => setMobileOpen(false)} aria-label="Close" className="text-gray-300 p-1 rounded-md hover:bg-gray-800/60">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>

            <div className="space-y-2">
              {isLoggedIn && user?.role === 'student' && (
                <>
                  <Link to="/search" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-indigo-600 text-white text-sm">Get Notes</Link>
                  <Link to="/profile" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-sm">Profile</Link>
                  <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="block w-full text-center px-2 py-2 rounded-lg border border-red-500 text-red-300 text-sm">Logout</button>
                </>
              )}

              {isLoggedIn && user?.role === 'admin' && (
                <>
                  <button
                    onClick={toggleMaintenance}
                    className={`w-full text-center px-2 py-2 rounded-lg border ${settings?.maintenanceMode ? 'border-amber-500 text-amber-500 bg-amber-500/10' : 'border-gray-700 text-gray-400 hover:bg-gray-800'} transition text-sm mb-2`}
                  >
                    {settings?.maintenanceMode ? '🛠 Maintenance ON' : '🛠 Maintenance OFF'}
                  </button>
                  <Link to="/branches" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-indigo-600 text-white text-sm">Upload</Link>
                  <Link to="/admin/purchases" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-sm">Purchases</Link>
                  <Link to="/admin/users" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-sm">Users</Link>
                  <Link to="/admin/pdfs" onClick={() => setMobileOpen(false)} className="block w-full text-center px-2 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-200 text-sm">PDFs</Link>
                  <button onClick={() => { setMobileOpen(false); handleLogout(); }} className="block w-full text-center px-2 py-2 rounded-lg border border-red-500 text-red-300 text-sm">Logout</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
